import type { ReactNode } from 'react';
import { Dialog as D } from 'radix-ui';
import { XIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/cn';

export const DialogRoot = D.Root;
export const DialogTrigger = D.Trigger;
export const DialogClose = D.Close;

export interface DialogProps {
  open?: boolean;
  onOpenChange?: (o: boolean) => void;
  trigger?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** Eyebrow above the title. */
  eyebrow?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closeLabel?: string;
}

const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

export const overlayCls =
  'fixed inset-0 z-50 bg-overlay data-[state=open]:animate-[gh-fade_180ms_ease-out] data-[state=closed]:animate-[gh-fade_120ms_reverse]';

export function Dialog({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  eyebrow,
  children,
  footer,
  size = 'md',
  closeLabel = 'Κλείσιμο',
}: DialogProps) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <D.Trigger asChild>{trigger}</D.Trigger>}
      <D.Portal>
        <D.Overlay className={overlayCls} />
        <D.Content
          className={cn(
            'fixed left-1/2 top-[min(12vh,7rem)] z-50 w-[calc(100vw-2rem)] -translate-x-1/2',
            'flex max-h-[calc(100dvh-min(12vh,7rem)-2rem)] flex-col rounded-lg border border-hairline bg-surface shadow-lg',
            'data-[state=open]:animate-[gh-dialog_220ms_var(--gh-ease-out)] focus:outline-none',
            widths[size],
          )}
        >
          <div className="flex items-start justify-between gap-6 px-6 pb-4 pt-5">
            <div className="grid gap-1">
              {eyebrow && <div className="eyebrow">{eyebrow}</div>}
              <D.Title className="font-display-tight text-xl text-ink">{title}</D.Title>
              {description ? (
                <D.Description className="text-[0.875rem] text-ink-muted">
                  {description}
                </D.Description>
              ) : (
                <D.Description className="sr-only">
                  {typeof title === 'string' ? title : ''}
                </D.Description>
              )}
            </div>
            <D.Close
              aria-label={closeLabel}
              className="-mr-2 -mt-1 rounded-sm p-1.5 text-ink-subtle transition-colors hover:bg-sunken hover:text-ink focus-visible:outline-2 focus-visible:outline-focus"
            >
              <XIcon className="size-[18px]" />
            </D.Close>
          </div>
          {children && <div className="min-h-0 overflow-y-auto px-6 pb-6">{children}</div>}
          {footer && (
            <div className="flex flex-wrap items-center justify-end gap-2 rounded-b-lg border-t border-hairline bg-canvas/70 px-6 py-3.5">
              {footer}
            </div>
          )}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
